/// REDUX ///
import { useAppDispatch } from "api/hooks/apiHook";
import { useAppSelector } from "api/hooks/apiHook";
/// REACT ///
import { useEffect } from "react";
import { useState } from "react";
/// ROUTER ///
import { useParams } from "react-router-dom";
/// TYPES ///
import { IPriceControl } from "api/types/price_list/price_control";
/// ACTIONS ///
import { getPriceList } from "api/actions/price_list";
import { getPriceControls } from "api/actions/price_list";
import { updatePriceControls } from "api/actions/price_list";
import { getShopCooktopCutouts } from "api/actions/price_list";
import { getShopFaucetCutouts } from "api/actions/price_list";
import { getShopSinkCutouts } from "api/actions/price_list";
import { getShopOutletCutouts } from "api/actions/price_list";
import { setBreadcrumbs } from "api/actions/ui/set";
/// MUI ///
import { Box } from "@mui/material";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
/// COMPONENTS ///
import CooktopCutout from "./cooktop_cutout";
import FaucetCutout from "./faucet_cutout";
import OutletCutout from "./outlet_cutout";
import SinkCutout from "./sink_cutout";
import Loading from "components/universal/loading";
import PageTitle from "components/universal/page/title";
import PriceControlSwitch from "components/price_lists/price_control/switch";
/// FM ///
import { motion } from "framer-motion";
/// STYLES ///
import { content_container_style } from "styles/universal/page";
import { header_style } from "styles/universal/section_header";
import { list_container_style } from "styles/universal/page";
import { title_style } from "styles/quote_preview/section";
/// MOTION ///
import { m_init_page } from "motion/page";
import { m_anim_page } from "motion/page";
import { m_exit_page } from "motion/page";
import { page_trans } from "motion/page";
/// BREADCRUMBS ///
import { list } from "values/breadcrumbs/market/pl";
import { overview } from "values/breadcrumbs/market/pl";

export default function CutoutPriceList() {
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const { price_list } = useAppSelector(state => state.price_list);
  const { cooktop_cutouts } = useAppSelector(state => state.price_list);
  const { faucet_cutouts } = useAppSelector(state => state.price_list);
  const { outlet_cutouts } = useAppSelector(state => state.price_list);
  const { sink_cutouts } = useAppSelector(state => state.price_list);
  const { price_controls } = useAppSelector(state => state.price_list);
  const { updating } = useAppSelector(state => state.price_list);
  const [update_price_control, setUpdatePriceControl] = useState<IPriceControl>({});

  useEffect(() => {
    setUpdatePriceControl(price_controls);
  }, [price_controls?.id]);

  useEffect(() => {
    if (!(price_list?.id) || price_list.id.toString() !== id) {
      dispatch(getPriceList(id));
    }
  }, [dispatch, id, price_list?.id]);

  useEffect(() => {
    if (id) {
      dispatch(getPriceControls());
      dispatch(getShopCooktopCutouts(id));
      dispatch(getShopFaucetCutouts(id));
      dispatch(getShopOutletCutouts(id));
      dispatch(getShopSinkCutouts(id));
    }
    dispatch(setBreadcrumbs([list, { ...overview, url: overview.url.replace("id", id) }]));
  }, [id, dispatch]);

  return (
    <motion.div initial={m_init_page} exit={m_exit_page} animate={m_anim_page} transition={page_trans} style={content_container_style}>
      <Grid container sx={list_container_style}>
        <PageTitle title={`${price_list?.name} Cutouts` ?? ""} endProps={<div></div>} />
        <Grid container item xs={12} justifyContent="space-between">
          <Grid item xs={12}>
            <Box sx={header_style}></Box>
          </Grid>
          <Grid item xs={6} sx={{ marginTop: "10px", marginBottom: "15px" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h6" sx={title_style}>
                Outlet Cutouts
              </Typography>
              <PriceControlSwitch
                checked={update_price_control?.use_outlet_cutout}
                onChange={checked => {
                  setUpdatePriceControl({ ...update_price_control, use_outlet_cutout: checked });
                  dispatch(updatePriceControls({ use_outlet_cutout: checked }, price_controls?.id));
                }} />
            </Stack>
          </Grid>
          <Grid item xs={6}></Grid>
          {
            update_price_control?.use_outlet_cutout ?
              outlet_cutouts.map((outlet_cutout, index) => <OutletCutout key={index} index={index} outlet_cutout={outlet_cutout} last={index === outlet_cutouts.length - 1} />) : null
          }
          <Grid item xs={6} sx={{ marginTop: "10px", marginBottom: "15px" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h6" sx={title_style}>
                Faucet Cutouts
              </Typography>
              <PriceControlSwitch
                checked={update_price_control?.use_faucet_cutout}
                onChange={checked => {
                  setUpdatePriceControl({ ...update_price_control, use_faucet_cutout: checked });
                  dispatch(updatePriceControls({ use_faucet_cutout: checked }, price_controls?.id));
                }} />
            </Stack>
          </Grid>
          <Grid item xs={6}></Grid>
          {
            update_price_control?.use_faucet_cutout ?
              faucet_cutouts.map((faucet_cutout, index) => <FaucetCutout key={index} index={index} faucet_cutout={faucet_cutout} last={index === faucet_cutouts.length - 1} />) : null
          }
          <Grid item xs={6} sx={{ marginTop: "10px", marginBottom: "15px" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h6" sx={title_style}>
                Cooktop Cutouts
              </Typography>
              <PriceControlSwitch
                checked={update_price_control?.use_cooktop_cutout}
                onChange={checked => {
                  setUpdatePriceControl({ ...update_price_control, use_cooktop_cutout: checked });
                  dispatch(updatePriceControls({ use_cooktop_cutout: checked }, price_controls?.id));
                }} />
            </Stack>
          </Grid>
          <Grid item xs={6}></Grid>
          {
            update_price_control?.use_cooktop_cutout ?
              cooktop_cutouts.map((cooktop_cutout, index) => <CooktopCutout key={index} index={index} cooktop_cutout={cooktop_cutout} last={index === cooktop_cutouts.length - 1} />) : null
          }
          <Grid item xs={6} sx={{ marginTop: "10px", marginBottom: "15px" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h6" sx={title_style}>
                Sink Cutouts
              </Typography>
              <PriceControlSwitch
                checked={update_price_control?.use_sink_cutout}
                onChange={checked => {
                  setUpdatePriceControl({ ...update_price_control, use_sink_cutout: checked });
                  dispatch(updatePriceControls({ use_sink_cutout: checked }, price_controls?.id));
                }} />
            </Stack>
          </Grid>
          <Grid item xs={6}></Grid>
          {
            update_price_control?.use_sink_cutout ?
              sink_cutouts.map((sink_cutout, index) => <SinkCutout key={index} index={index} sink_cutout={sink_cutout} last={index === sink_cutouts.length - 1} />) : null
          }
        </Grid>
      </Grid>
      <Loading loading={updating} />
    </motion.div>
  )
}
